import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Target,
  PenTool,
  Megaphone,
  BarChart3,
  LineChart,
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import pic1 from "../../../assets/images/content righting.jpg";

const Content = () => {
  // Foundation pillars data
  const content = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: "🔍 Comprehensive Content Audit",
      description:
        "See what’s working — and what’s not. We begin by thoroughly evaluating your existing content. Our audits highlight gaps, outdated material, performance issues, and opportunities for improvement. You’ll receive a detailed report that guides your future content roadmap with precision.",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "🧭 Strategic Content Planning",
      description:
        "Content with a clear purpose and measurable results. Once the audit is complete, we build a tailored strategy that aligns with your business objectives. From keyword-driven topic ideation to content formats and publishing schedules, we craft a plan that keeps your brand ahead of the curve.",
    },
    {
      icon: <PenTool className="h-8 w-8" />,
      title: "✍️ Engaging Content Creation",
      description:
        "Tell your story in a way that captivates. Our skilled writers, designers, and content creators deliver impactful content across platforms: blogs, infographics, videos, landing pages, and more. Every piece is optimized for SEO and user experience to attract and retain your target audience.",
    },
    {
      icon: <Megaphone className="h-8 w-8" />,
      title: "📣 Multi-Channel Content Promotion",
      description:
        "Get your message in front of the right audience. We don’t just publish content — we amplify it. Through strategic social media campaigns, influencer outreach, email marketing, and paid promotion, we make sure your content gets the reach and engagement it deserves.",
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "🔧 Content Optimization & SEO Integration",
      description:
        "Maximize visibility and usability. Using best-in-class SEO practices, we optimize content for search engines and social platforms. Mobile-responsive design, keyword optimization, internal linking, and fast load speeds ensure a seamless user experience and improved rankings.",
    },
    {
      icon: <LineChart className="h-8 w-8" />,
      title: "📊 Analytics & Reporting",
      description:
        "Content that evolves through data. We track critical performance metrics like engagement, traffic, conversions, and bounce rates. Monthly reports keep you in the loop and provide actionable insights for refining your content strategy",
    },
  ];

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="bg-gray-100 text-gray-800" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  {" "}
                  🚀 Power Your Brand with Strategic{" "}
                </span>
                <span className="text-blue-600"> Content Management</span>
              </h1>
              <p className="mt-4 mb-4 font-bold">
                Make Your Content Work for You — Consistently and Creatively
              </p>
              <p className="text-white mb-8">
                At Right On Time Services, we understand that in a crowded
                digital world, content isn’t just king — it’s your brand’s
                voice. Our comprehensive Content Management Services ensure your
                message is not only heard but remembered. From strategy to
                creation, promotion to optimization — we’ve got everything
                covered so you can focus on growing your business.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      {/* About Section */}
      <section className="py-16 bg-white" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-blue-900 text-center">
          Content Management Services
        </h2>
        <p className="mt-4 text-lg max-w-4xl mx-auto">
          In today's digital age, creating and distributing quality content is a
          critical part of any successful marketing strategy. However, with so
          much content out there, it can be challenging to stand out and get
          noticed by your target audience. At 24-7 Consultancy, we understand
          that managing your content can be a daunting task, which is why we
          offer a range of Content Management Services to help you stay on top
          of your digital marketing game. Our team of experienced digital
          marketing experts can help you create, manage, and promote your
          content, so you can focus on running your business. Whether you need
          help with content strategy, creation, promotion, or optimization,
          we've got you covered.
        </p>
      </section>

      {/* Why Choose Us? */}
      <motion.div
        className="p-6  bg-gray-50 rounded-lg transition duration-300"
        whileHover={{ scale: 1.05 }}
        data-aos="fade-up"
      >
        <div
          className="py-20 bg-gray-50 duration-1000 mb-16"
          data-aos="fade-up"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                📚 What We Offer: End-to-End CMS Solutions
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {content.map((foundation, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-lg shadow-lg p-8 transition-all duration-500 hover:shadow-lg hover:-translate-y-2"
                  style={{ transitionDelay: `${index * 100}ms` }}
                  data-aos="fade-up"
                >
                  <div className="text-blue-600 mb-4 flex justify-center">
                    <div className="p-3 bg-blue-100 rounded-full">
                      {/* {foundation.icon} */}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-center">
                    {foundation.title}
                  </h3>
                  <p className="text-gray-600">{foundation.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="p-6  bg-gray-50 rounded-lg transition duration-300"
        whileHover={{ scale: 1.05 }}
        data-aos="fade-up"
      >
        <section className="py-20 bg-gray-50 px-4 md:px-10" data-aos="fade-up">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold text-blue-700 mb-6 text-left">
                💡 Why Choose Right On Time Services for CMS?
              </h2>
              <div className="grid gap-6 text-left">
                <div className="bg-white p-6 rounded-2xl shadow-md w-full max-w-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-2">
                    ✅ All-in-One Service
                  </h4>
                  <p className="text-gray-700 text-sm">
                    From ideation to execution, everything under one roof.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-md w-full max-w-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-2">
                    ✅ Expert Team
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Digital strategists, SEO experts, designers, and
                    storytellers at your service.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-md w-full max-w-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-2">
                    ✅ Custom Solutions
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Every brand is unique, and so is our approach.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-md w-full max-w-sm">
                  <h4 className="text-xl font-semibold text-blue-800 mb-2">
                    ✅ Results-Driven
                  </h4>
                  <p className="text-gray-700 text-sm">
                    We focus on engagement, visibility, and ROI.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <img
                src={pic1} // ← Replace with your actual image path
                alt="CMS Service Illustration"
                className="w-full max-w-md h-auto object-contain rounded-xl"
              />
            </motion.div>
          </div>
        </section>
      </motion.div>

      <motion.section
        className="py-16 text-center bg-white rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        data-aos="fade-up"
      >
        <section className="py-20 bg-white px-4 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-blue-700 mb-6">
              ✅ Ready to Transform Your Content Strategy?
            </h2>
            <p className="text-gray-700 text-md mb-4">
              Let’s craft content that converts and stories that stick.
            </p>
            <p className="text-lg font-semibold text-blue-800">
              📞 Contact Right On Time Services today and let's get started.
            </p>
          </div>
        </section>
      </motion.section>
    </div>
  );
};

export default Content;
